import { MEMBERS_LIST_PAGE_SIZE } from "@/config/constants";
import { useNavigationState } from "@/zustand/NavigationState";
import { useInfiniteQuery } from "@tanstack/react-query";
import { getGroupChatMembers } from "../members";

interface UsePaginatedMembersByChatIdOptions {
  chatId: string;
  count?: number;
}

export function usePaginatedMembersByChatId({
  chatId,
  count = MEMBERS_LIST_PAGE_SIZE,
}: UsePaginatedMembersByChatIdOptions) {
  const showChatDetails = useNavigationState((state) => state.showChatDetails);

  // Set up the infinite query
  const {
    data,
    status,
    error,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isPending,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["chat-members", chatId],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getGroupChatMembers(chatId, pageParam, count);
      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < count) {
        return undefined;
      }
      return allPages.length * count;
    },
    initialPageParam: 0, // initial offset skip
    // Only enable the query if there is an active chat and the chat details are shown.
    enabled: !!chatId && showChatDetails,
    // TO DO => To be configured later based on estimated usage and best performance
    // staleTime: 60000,
    // gcTime: 1000, // Inactive queries are garbage collected after 5 minutes by default.
    // refetchInterval: 10000,
    // refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    data,
    status,
    isFetching,
    isLoading,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  };
}
